import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../lib/api';
import type { SearchResults } from '../lib/types';
import { Dialog } from './ui/dialog';
import { Input } from './ui/input';

/** Поиск Cmd+K / Ctrl+K: заказы, товары, клиенты. */
export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(q.trim());
    }, 200);
    return () => {
      clearTimeout(timer);
    };
  }, [q]);

  const results = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api<SearchResults>(`/search?q=${encodeURIComponent(debounced)}`),
    enabled: open && debounced.length >= 2,
  });
  const close = () => {
    setOpen(false);
    setQ('');
  };
  const go = (to: () => Promise<void>) => {
    close();
    void to();
  };
  const r = results.data;
  const empty = r && !r.orders.length && !r.products.length && !r.customers.length;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        className="flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent"
      >
        <Search className="size-4" />
        {t('cmdk.open')}
        <kbd className="ml-auto text-xs">Ctrl K</kbd>
      </button>
      {open ? (
        <Dialog open onClose={close} title={t('cmdk.title')} className="max-w-xl">
          <div className="grid gap-3 text-sm">
            <Input
              aria-label={t('cmdk.placeholder')}
              placeholder={t('cmdk.placeholder')}
              value={q}
              autoFocus
              onChange={(e) => {
                setQ(e.target.value);
              }}
            />
            {r?.orders.length ? (
              <section>
                <p className="mb-1 text-xs uppercase text-muted-foreground">{t('nav.orders')}</p>
                {r.orders.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className="block w-full rounded px-2 py-1 text-left hover:bg-accent"
                    onClick={() => {
                      go(() => navigate({ to: '/orders/$orderId', params: { orderId: o.id } }));
                    }}
                  >
                    {o.number} · {o.email} · {o.status}
                  </button>
                ))}
              </section>
            ) : null}
            {r?.products.length ? (
              <section>
                <p className="mb-1 text-xs uppercase text-muted-foreground">{t('nav.products')}</p>
                {r.products.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="block w-full rounded px-2 py-1 text-left hover:bg-accent"
                    onClick={() => {
                      go(() =>
                        navigate({ to: '/products/$productId', params: { productId: p.id } }),
                      );
                    }}
                  >
                    {p.name ?? '—'} · <span className="font-mono">{p.sku}</span>
                  </button>
                ))}
              </section>
            ) : null}
            {r?.customers.length ? (
              <section>
                <p className="mb-1 text-xs uppercase text-muted-foreground">{t('nav.customers')}</p>
                {r.customers.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="block w-full rounded px-2 py-1 text-left hover:bg-accent"
                    onClick={() => {
                      go(() =>
                        navigate({ to: '/customers/$customerId', params: { customerId: c.id } }),
                      );
                    }}
                  >
                    {c.email}
                  </button>
                ))}
              </section>
            ) : null}
            {empty ? <p className="text-muted-foreground">{t('cmdk.nothing')}</p> : null}
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
